import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import MenuOverlay from './MenuOverlay'

/**
 * Generic Sidebar Component.
 *
 * Sidebar component currently only supports left pull out sidebars, but this
 * could be updated in the future to have sidebars from all sides of the screen.
 *
 * @param {object} - Contains current context, isOpen, side, and toggleSidebar states
 *
 * @returns {function} - React Component
 */
const MobileSidebar = ({ context, isOpen, side, toggleSidebar, children }) => {
  /**
   * Keypress Event Handler. Checks to see if the Esc key was pressed. Once the
   * Esc key is pressed, the sidebar should be closed.
   *
   * @param {object} e - Escape keypress event
   */
  const escapeKeyPress = e => {
    if (e.keyCode === 27) {
      toggleSidebar('Escape Key')
    }
  }

  /**
   * Adds or removes behavior based on if the sidebar is about to be opened, or
   * closed. Current functionality includes:
   * - Esc key to close sidebar.
   * - Stop scrolling of background while sidebar is open.
   * - Toggle visibility of CS chat box.
   */
  useEffect(() => {
    const documentContext = document.querySelector(context)

    // Sidebar is open
    if (isOpen) {
      // Add Esc key listener to close sidebar.
      window.addEventListener('keydown', escapeKeyPress, false)

      // Stop user from being able to scroll context.
      if (documentContext) {
        documentContext.style.overflowY = 'hidden'
      }

      // Hide the chat button since its z-index can't be trumped.
      if (window.CustomerSupport) {
        window.CustomerSupport.closeChat()
        window.CustomerSupport.hideChatBubble()
      }
    }

    // Sidebar is Closed
    if (!isOpen) {
      // Remove Esc key listener to close sidebar.
      window.removeEventListener('keydown', escapeKeyPress)

      // Allow context to scroll again.
      if (documentContext) {
        documentContext.style.overflowY = 'inherit'
      }

      // Show the chat button again.
      if (window.CustomerSupport) {
        window.CustomerSupport.showChatBubble()
      }
    }
  }, [isOpen])

  return (
    <div
      className={`sidebar is-${side}-sidebar ${isOpen ? 'is-open' : 'is-closed'}`}
      aria-hidden={!isOpen}
    >
      <MenuOverlay isVisible={isOpen} onClick={() => toggleSidebar('Overlay')} zIndex={5} />
      <div className="sidebar-content">{children}</div>
    </div>
  )
}

MobileSidebar.defaultProps = {
  isOpen: false,
  side: 'left',
  context: 'body',
}

MobileSidebar.propTypes = {
  isOpen: PropTypes.bool,
  toggleSidebar: PropTypes.func.isRequired,
  side: PropTypes.string,
  context: PropTypes.string,
  children: PropTypes.node,
}

export default MobileSidebar
